import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";

export default function TicketModal({
  open,
  onClose,
  onSave,
  initialData,
  clients = [],
  contacts = [],
  collaborateurs = [],
  statuts = [],
  onUploadFiles,
  inline = false,
}) {
  const { user } = useAuth();
  const isCollaborateur = user?.type === 'collaborateur';
  const isContact = user?.type === 'contact';
  const { showToast } = useToast();

  const [form, setForm] = useState({
    titre: "",
    description: "",
    client: "",
    contact: "",
    collaborateur: "",
    statut: "",
    priorite: 3,
    tpsResolution: "",
    note: "",
  });
  const [errors, setErrors] = useState({ titre: "", description: "" });
  const [files, setFiles] = useState([]);
  const [fileNames, setFileNames] = useState("");
  const tpsResolutionRef = useRef(null);
  const noteRef = useRef(null);

  const selectedStatut = statuts.find(
    (s) => String(s.id) === String(form.statut)
  );
  const isAffecteStatus =
    !!selectedStatut &&
    !!selectedStatut.libelle &&
    selectedStatut.libelle.toLowerCase().includes("affect");

  // Trouver le statut "Demande reçue" par défaut
  const defaultStatutId = statuts.find(s => 
    s.libelle && s.libelle.toLowerCase().includes('demande reçue')
  )?.id || statuts.find(s => s.id === 1)?.id || "";

  useEffect(() => {
    if (initialData) {
      setForm({
        titre: initialData.titre || "",
        description: initialData.description || "",
        client: initialData.client || "",
        contact: initialData.contact || "",
        collaborateur: initialData.collaborateur || "",
        statut: initialData.statut || "",
        priorite: initialData.priorite ?? 3,
        tpsResolution: initialData.tpsResolution ?? "",
        note: initialData.note || "",
      });
      setErrors({ titre: "", description: "" });
      setFiles([]);
      } else {
        // Si c'est un contact qui crée un ticket, préremplir les champs
        if (isContact && user) {
          const clientId = String(user.clientId || "");
          const contactId = String(user.id || "");
          setForm({
            titre: "",
            description: "",
            client: clientId,
            contact: contactId,
            collaborateur: "",
            statut: defaultStatutId ? String(defaultStatutId) : "",
            priorite: 3,
            tpsResolution: "",
            note: "",
          });
        } else if (isCollaborateur && user && user.id) {
          // Si c'est un collaborateur qui crée un ticket, préremplir le collaborateur avec lui-même
          const collaborateurId = String(user.id || "");
          setForm({
            titre: "",
            description: "",
            client: "",
            contact: "",
            collaborateur: collaborateurId,
            statut: "",
            priorite: 3,
            tpsResolution: "",
            note: "",
          });
        } else {
          setForm({
            titre: "",
            description: "",
            client: "",
            contact: "",
            collaborateur: "",
            statut: "",
            priorite: 3,
            tpsResolution: "",
            note: "",
          });
        }
        setErrors({ titre: "", description: "" });
        setFiles([]);
      }
  }, [initialData, open, isContact, isCollaborateur, user, defaultStatutId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si le client change, réinitialiser le contact
    if (name === "client") {
      setForm({ ...form, [name]: value, contact: "" });
    } else {
      setForm({ ...form, [name]: value });
    }
    
    if (name === "titre" || name === "description") {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleFiles = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles(selectedFiles);
    if (selectedFiles.length === 0) {
      setFileNames("");
    } else if (selectedFiles.length === 1) {
      setFileNames(selectedFiles[0].name);
    } else {
      setFileNames(`${selectedFiles.length} fichiers sélectionnés`);
    }
  };

  // Filtrer les contacts en fonction du client sélectionné
  const filteredContacts = form.client 
    ? contacts.filter(contact => {
        // Vérifier si le contact appartient au client sélectionné
        const contactClientId = contact.clientId;
        const selectedClientId = form.client;
        
        // Gérer les IRI (format /api/clients/123)
        if (typeof contactClientId === 'string' && contactClientId.includes('/api/clients/')) {
          const clientIdFromIri = contactClientId.split('/api/clients/')[1];
          return clientIdFromIri === selectedClientId;
        }
        
        // Gérer les IDs directs
        return contactClientId === selectedClientId || contact.client === selectedClientId;
      })
    : contacts;

  const handleSubmit = async () => {
    const nextErrors = { titre: "", description: "" };
    if (!form.titre) nextErrors.titre = "Titre requis";
    if (!form.description) nextErrors.description = "Description requise";
    setErrors(nextErrors);
    if (nextErrors.titre || nextErrors.description) return;

    if (isAffecteStatus && !form.collaborateur) {
      showToast("Veuillez sélectionner un collaborateur pour ce statut.", {
        severity: "warning",
      });
      return;
    }

    // Si un collaborateur met le ticket en "Terminée", le temps de résolution et la note interne sont obligatoires
    if (isCollaborateur && !isContact) {
      const currentStatus = statuts.find(
        (s) => String(s.id) === String(form.statut)
      );
      const isTerminee =
        currentStatus &&
        currentStatus.libelle &&
        currentStatus.libelle.toLowerCase().includes("termin");
      if (isTerminee) {
        if (form.tpsResolution === "" || form.tpsResolution === null) {
          if (tpsResolutionRef.current) {
            tpsResolutionRef.current.focus();
          }
          showToast(
            "Le temps de résolution est obligatoire pour un ticket terminé.",
            { severity: "warning" }
          );
          return;
        }
        if (!form.note || !form.note.trim()) {
          if (noteRef.current) {
            noteRef.current.focus();
          }
          showToast("La note interne est obligatoire pour un ticket terminé.", {
            severity: "warning",
          });
          return;
        }
      }
    }

    const result = await onSave(form);
    if (result && result.id && files.length && onUploadFiles) {
      await onUploadFiles(result.id, files);
    }
  };

  // Libellé du statut courant (pour affichage en lecture seule côté contact)
  const currentStatusLabel =
    statuts.find((s) => String(s.id) === String(form.statut))?.libelle || "";

  const formFields = (
    <>
      <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, color: "text.secondary", fontWeight: 600 }}>
        Informations principales
      </Typography>
      <TextField
        margin="dense"
        label="Titre"
        name="titre"
        value={form.titre}
        onChange={handleChange}
        fullWidth
        required
        error={Boolean(errors.titre)}
        helperText={errors.titre}
      />
      <TextField
        margin="dense"
        label="Description"
        name="description"
        value={form.description}
        onChange={handleChange}
        fullWidth
        multiline
        minRows={3}
        required
        error={Boolean(errors.description)}
        helperText={errors.description}
      />
      {isCollaborateur ? (
        <FormControl fullWidth margin="dense">
          <InputLabel id="statut-label">Statut *</InputLabel>
          <Select
            labelId="statut-label"
            label="Statut *"
            name="statut"
            value={form.statut}
            onChange={handleChange}
          >
            {statuts.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.libelle || s.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          margin="dense"
          label="Statut *"
          value={currentStatusLabel || "Demande reçue"}
          fullWidth
          disabled
        />
      )}
      {isCollaborateur && isAffecteStatus && (
        <FormControl fullWidth margin="dense">
          <InputLabel id="collaborateur-label">Collaborateur *</InputLabel>
          <Select
            labelId="collaborateur-label"
            label="Collaborateur *"
            name="collaborateur"
            value={form.collaborateur}
            onChange={handleChange}
          >
            {collaborateurs
              .slice()
              .sort((a, b) => {
                const nameA = `${a.prenom || ""} ${a.nom || ""}`.trim();
                const nameB = `${b.prenom || ""} ${b.nom || ""}`.trim();
                return nameA.localeCompare(nameB);
              })
              .map((col) => (
                <MenuItem key={col.id} value={String(col.id)}>
                  {`${col.prenom || ""} ${col.nom || ""}`.trim() || col.id}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}
      <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, color: "text.secondary", fontWeight: 600 }}>
        Contexte client
      </Typography>
      <FormControl fullWidth margin="dense">
        <InputLabel id="client-label">Client *</InputLabel>
        <Select
          labelId="client-label"
          label="Client *"
          name="client"
          value={form.client}
          onChange={handleChange}
          disabled={isContact}
        >
          {clients.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.nom || c.id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth margin="dense">
        <InputLabel id="priorite-label">Priorité</InputLabel>
        <Select
          labelId="priorite-label"
          label="Priorité"
          name="priorite"
          value={form.priorite}
          onChange={handleChange}
        >
          <MenuItem value={1}>Priorité 1 — Critique / Urgente</MenuItem>
          <MenuItem value={2}>Priorité 2 — Haute</MenuItem>
          <MenuItem value={3}>Priorité 3 — Moyenne</MenuItem>
          <MenuItem value={4}>Priorité 4 — Basse</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth margin="dense">
        <InputLabel id="contact-label">Contact</InputLabel>
        <Select
          labelId="contact-label"
          label="Contact"
          name="contact"
          value={form.contact}
          onChange={handleChange}
          disabled={!form.client || isContact}
        >
          {!form.client && (
            <MenuItem value="" disabled>
              Sélectionnez d'abord un client
            </MenuItem>
          )}
          {filteredContacts.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {`${c.prenom || ""} ${c.nom || ""}`.trim() || c.id}
            </MenuItem>
          ))}
          {form.client && filteredContacts.length === 0 && (
            <MenuItem value="" disabled>
              Aucun contact trouvé pour ce client
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {isCollaborateur && !isContact && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, color: "text.secondary", fontWeight: 600 }}>
            Suivi interne
          </Typography>
          <TextField
            margin="dense"
            label="Temps de résolution (min)"
            name="tpsResolution"
            type="number"
            value={form.tpsResolution}
            onChange={handleChange}
            fullWidth
            inputRef={tpsResolutionRef}
          />
          <TextField
            margin="dense"
            label="Note interne"
            name="note"
            value={form.note}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            inputRef={noteRef}
          />
        </>
      )}
      <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, color: "text.secondary", fontWeight: 600 }}>
        Pièces jointes
      </Typography>
      <Box sx={{ mt: 1, p: 1.5, border: (theme) => `1px dashed ${theme.palette.divider}`, borderRadius: 1.5 }}>
        <input
          id="ticket-file-input"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFiles}
        />
        <Button
          variant="outlined"
          component="label"
          htmlFor="ticket-file-input"
        >
          Sélectionner des fichiers
        </Button>
        <Box component="span" sx={{ ml: 2, fontSize: 14, color: "text.secondary" }}>
          {fileNames || "Aucun fichier choisi"}
        </Box>
      </Box>
    </>
  );

  if (inline) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {initialData ? "Modifier" : "Créer"} un ticket
        </Typography>
        <Box>{formFields}</Box>
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {initialData ? "Modifier" : "Créer"}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 2 }}>{initialData ? "Modifier" : "Créer"} un ticket</DialogTitle>
      <DialogContent dividers>{formFields}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained">
          {initialData ? "Modifier" : "Créer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
} 